import { useEffect, useRef } from "react";
import { deviceType, primaryInput } from "detect-it";
import { WebHaptics } from "web-haptics";
// Long-press delays split by input device because the gesture lives
// next to dnd-kit's per-input drag activation in `<Stream>` — on
// touch the two timers are staggered so a single stationary hold
// escalates cleanly from "drag pickup" into "multi-select."
//
//   - Mouse: 300ms. Desktop has no scroll-vs-drag conflict, so the
//     long-press timer is the only thing waiting on the press.
//   - Touch: 650ms. The dnd-kit `TouchSensor` picks up a drag at
//     ~300ms of stationary hold; sitting multi-select 350ms past
//     that gives the two state changes clear breathing room so
//     they read as separate moments rather than a continuous
//     hold. The user can still "rescue" out of multi-select by
//     moving the finger — `<Stream>`'s `handleDragMove` exits
//     multi-select on first movement so the in-flight drag wins
//     (`useSortable` is no longer disabled while in multi-select,
//     so the activated drag stays armed throughout the press).
// NOTE: This is funny ... it doesn't actually feel like 100ms.
//       It feels like it's 100ms _on top_ of whatever the long drag is.
const MOUSE_LONG_PRESS_DELAY_MS = 200;
const TOUCH_LONG_PRESS_DELAY_MS = 500;
// Cap on what counts as a "tap" on touch. Aligns with the dnd-kit
// `TouchSensor` activation delay in `<Stream>` so the press timeline
// reads as three disjoint windows:
//
//   - 0 – 299ms held: tap-eligible (releases here = open the card).
//   - 300 – 649ms held still: drag-pickup territory.
//   - 650ms+ held still: long-press multi-select (or, if the
//     finger moves after the multi-select fires, drag rescue).
//
// Without this cap a release at e.g. 500ms — well into drag-pickup
// territory — would also fire `onTap` (because the long-press hadn't
// completed yet), so the card would briefly lift for the drag and
// then also open in a modal. Trimming the tap window to match the
// drag-pickup delay keeps the three press outcomes mutually
// exclusive.
const TOUCH_TAP_MAX_HOLD_MS = 300;
// Movement that cancels the long-press timer. Per-input thresholds
// because long-press lives next to dnd-kit's `MouseSensor` /
// `TouchSensor` on the same DOM nodes — anything that fires
// `useSortable`'s activation handler must also kill the long-press
// timer, or both gestures will race during a slow drag (drag
// captures the pointer, then the long-press timer fires multiselect
// mid-drag).
//
//   - Mouse: 0px → any non-zero movement cancels. Aligns with
//     `<Stream>`'s `MouseSensor` at `distance: 1` (drag activates
//     on the first 1px movement; the long-press cancels at the
//     same moment).
//   - Touch: 3px → finger jitter inside this envelope keeps the
//     timer armed. Tighter than the dnd-kit `TouchSensor`
//     tolerance (8px) on purpose: any finger drift past 3px
//     means the user is no longer "stationary holding," so the
//     long-press escalation is the wrong target — the drag
//     pickup will still consume up-to-8px jitter inside its
//     own 200ms window.
const MOUSE_MOVE_CANCEL_THRESHOLD_PX = 0;
const TOUCH_MOVE_CANCEL_THRESHOLD_PX = 3;
const HAS_TOUCH_INPUT = deviceType === "touchOnly" || deviceType === "hybrid" || primaryInput === "touch";
let sharedHaptics;
function getHaptics() {
    if (!sharedHaptics)
        sharedHaptics = new WebHaptics();
    return sharedHaptics;
}
/**
 * Detects long-press on touch + mouse, and (optionally) taps on touch.
 *
 * This is the shared primitive behind both `useLongPressDrag` (cards
 * with drag binding) and `useLongPressCollectionSelect` (collection
 * cards). It owns the parts both flavors duplicated:
 *
 *   - 300ms long-press timer with a <10px movement cancel threshold
 *     on both touch and mouse
 *   - Click-event swallow on capture so the React `onClick` registered
 *     on the element does not also fire after the gesture completes
 *   - On touch, an opt-in tap path that bypasses iOS Safari's
 *     synthetic mouse compat events (see `onTap` docs above)
 *   - Haptic feedback on long-press fire
 *
 * Listeners are attached directly to the DOM node passed via `ref`.
 * Each instance is self-contained — multiple cards mounting this hook
 * in parallel each get their own timers / listener pair.
 */
export function useLongPress(ref, options) {
    const touchTimerRef = useRef(undefined);
    const mouseTimerRef = useRef(undefined);
    // Latest-callback refs so the consumer can pass inline closures
    // without busting the listener-attaching effect's dep array. Cards
    // re-rendering at high cadence (e.g. via Zustand subscriptions) would
    // otherwise tear the listeners off and re-attach on every render.
    const onLongPressRef = useRef(options.onLongPress);
    onLongPressRef.current = options.onLongPress;
    const onTapRef = useRef(options.onTap);
    onTapRef.current = options.onTap;
    const hapticRef = useRef(options.haptic);
    hapticRef.current = options.haptic;
    const enabled = options.enabled ?? true;
    // Touch path: long-press detection + (optional) tap detection.
    useEffect(() => {
        if (!enabled)
            return;
        if (HAS_TOUCH_INPUT === false)
            return;
        const el = ref.current;
        if (!el)
            return;
        let startX = 0;
        let startY = 0;
        let startTime = 0;
        // Movement is tracked separately from `touchTimerRef` so the tap
        // criterion is still answerable after the long-press timer was
        // cleared (e.g. by `onTouchMove` passing the cancel threshold).
        let movedPastTapThreshold = false;
        // Set when the long-press fired in this gesture. We swallow the
        // follow-up click so the consumer's React `onClick` doesn't run
        // (which would, e.g., toggle the just-selected card right back off).
        let triggered = false;
        // Set when the tap path below fires `onTap`. Same swallow purpose
        // as `triggered` — keeps the consumer's `onClick` from firing a
        // second action path on top of the explicit tap dispatch.
        let tapDispatched = false;
        const onTouchStart = (e) => {
            const touch = e.touches[0];
            if (!touch)
                return;
            startX = touch.clientX;
            startY = touch.clientY;
            startTime = performance.now();
            triggered = false;
            movedPastTapThreshold = false;
            tapDispatched = false;
            touchTimerRef.current = setTimeout(() => {
                const haptic = hapticRef.current;
                if (haptic)
                    getHaptics().trigger(haptic);
                triggered = true;
                onLongPressRef.current();
            }, TOUCH_LONG_PRESS_DELAY_MS);
        };
        const onTouchMove = (e) => {
            const touch = e.touches[0];
            if (!touch)
                return;
            const dx = touch.clientX - startX;
            const dy = touch.clientY - startY;
            if (dx * dx + dy * dy > TOUCH_MOVE_CANCEL_THRESHOLD_PX * TOUCH_MOVE_CANCEL_THRESHOLD_PX) {
                movedPastTapThreshold = true;
                if (touchTimerRef.current !== undefined) {
                    clearTimeout(touchTimerRef.current);
                    touchTimerRef.current = undefined;
                }
            }
        };
        const onTouchEnd = (e) => {
            const longPressFired = triggered;
            const heldFor = performance.now() - startTime;
            if (touchTimerRef.current !== undefined) {
                clearTimeout(touchTimerRef.current);
                touchTimerRef.current = undefined;
            }
            // Tap path runs only when the consumer opted in via `onTap`.
            // A tap is a quick stationary press that landed inside the
            // tap-eligible window (< TOUCH_TAP_MAX_HOLD_MS) — anything
            // longer falls into drag-pickup or long-press territory and
            // shouldn't double-fire as a tap. See the constant block at
            // the top of this file for the full press-timeline rationale.
            if (onTapRef.current &&
                longPressFired === false &&
                movedPastTapThreshold === false &&
                heldFor < TOUCH_TAP_MAX_HOLD_MS) {
                tapDispatched = true;
                // Cancel iOS Safari's compat mouse-event chain so the synthetic
                // `mousedown` ~50-300ms later doesn't land on whatever element
                // the consumer just opened over the tap point. Safe because
                // `touchend` has no scroll performance impact (unlike
                // `touchmove`) — we register non-passive only when an `onTap`
                // is provided so consumers that don't need this don't pay it.
                e.preventDefault();
                onTapRef.current();
            }
        };
        const cancel = () => {
            if (touchTimerRef.current !== undefined) {
                clearTimeout(touchTimerRef.current);
                touchTimerRef.current = undefined;
            }
        };
        // Capture-phase click handler — swallows the synthetic click so the
        // consumer's React `onClick` doesn't fire after a long-press or an
        // explicit tap dispatch (otherwise a long-press into multiselect
        // would also toggle the just-selected card off, and an explicit
        // `onTap` would also run the click handler's branch).
        const onClickCapture = (e) => {
            if (triggered === false && tapDispatched === false)
                return;
            triggered = false;
            tapDispatched = false;
            e.stopImmediatePropagation();
            e.preventDefault();
        };
        // touchend is non-passive only when `onTap` is wired so we can call
        // `preventDefault()`. Without `onTap`, leaving touchend passive lets
        // the consumer's React `onClick` run naturally for tap interactions.
        const touchEndPassive = onTapRef.current === undefined;
        el.addEventListener("touchstart", onTouchStart, { passive: true });
        el.addEventListener("touchmove", onTouchMove, { passive: true });
        el.addEventListener("touchend", onTouchEnd, { passive: touchEndPassive });
        el.addEventListener("touchcancel", cancel, { passive: true });
        el.addEventListener("click", onClickCapture, { capture: true });
        return () => {
            cancel();
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
            el.removeEventListener("touchend", onTouchEnd);
            el.removeEventListener("touchcancel", cancel);
            el.removeEventListener("click", onClickCapture, { capture: true });
        };
    }, [ref, enabled]);
    // Mouse path: long-press detection. Mouse cards never get the tap
    // path (React's onClick is reliable for mouse), so this path is the
    // same regardless of whether the consumer passed `onTap`.
    useEffect(() => {
        if (!enabled)
            return;
        if (HAS_TOUCH_INPUT)
            return;
        const el = ref.current;
        if (!el)
            return;
        let startX = 0;
        let startY = 0;
        let triggered = false;
        function onPointerMove(e) {
            if (mouseTimerRef.current === undefined)
                return;
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            if (dx * dx + dy * dy > MOUSE_MOVE_CANCEL_THRESHOLD_PX * MOUSE_MOVE_CANCEL_THRESHOLD_PX) {
                cancel();
            }
        }
        function cancel() {
            if (mouseTimerRef.current !== undefined) {
                clearTimeout(mouseTimerRef.current);
                mouseTimerRef.current = undefined;
            }
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", cancel);
            window.removeEventListener("pointercancel", cancel);
        }
        function onPointerDown(e) {
            if (e.pointerType !== "mouse")
                return;
            if (e.button !== 0)
                return;
            startX = e.clientX;
            startY = e.clientY;
            triggered = false;
            mouseTimerRef.current = setTimeout(() => {
                triggered = true;
                onLongPressRef.current();
            }, MOUSE_LONG_PRESS_DELAY_MS);
            window.addEventListener("pointermove", onPointerMove);
            window.addEventListener("pointerup", cancel);
            window.addEventListener("pointercancel", cancel);
        }
        // Capture-phase click swallow — same role as the touch path's, but
        // only fires when the long-press completed (mouse has no separate
        // tap-dispatch path because React's onClick is reliable here).
        function onClickCapture(e) {
            if (triggered === false)
                return;
            triggered = false;
            e.stopImmediatePropagation();
            e.preventDefault();
        }
        el.addEventListener("pointerdown", onPointerDown);
        el.addEventListener("click", onClickCapture, { capture: true });
        return () => {
            cancel();
            el.removeEventListener("pointerdown", onPointerDown);
            el.removeEventListener("click", onClickCapture, { capture: true });
        };
    }, [ref, enabled]);
}
